import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { MultiBackend, TouchTransition, MouseTransition } from 'react-dnd-multi-backend';

import Header from '../components/Header';
import { useStorage } from '../storage/StorageContext';
import { Team, FormationCell, Player } from '../types';
import { PRESET_TEAMS } from './OTTpresets';
import { OTTGrid } from './OTTGrid';
import { RosterBench } from '../components/RosterBench';
import '../App.css';

const HTML5toTouch = {
  backends: [
    {
      id: 'html5',
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      id: 'touch',
      backend: TouchBackend,
      options: {enableMouseEvents: true},
      preview: true,
      transition: TouchTransition,
    },
  ],
};

export default function OTTPage() {
  const navigate = useNavigate();
  const storage = useStorage();
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  
  // Simulator State
  const [layout, setLayout] = useState<FormationCell[]>([]);
  const [opponentLayout, setOpponentLayout] = useState<FormationCell[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  
  // Play Mode State
  const [isPlayMode, setIsPlayMode] = useState(false);
  
  // Push Selection State
  const [pushSelection, setPushSelection] = useState<{
    attackerId: number;
    defenderId: number;
    defenderIsOpponent: boolean;
    fromRow: number; // Attacker's original row
    fromCol: number; // Attacker's original col
    targetRow: number; // Defender's current row (where attacker goes)
    targetCol: number; // Defender's current col
    validSquares: { row: number; col: number }[];
  } | null>(null);

  useEffect(() => {
    storage.listTeams().then(setUserTeams);
  }, [storage]);

  // Helper to set up a standard OTT Offense
  const setupStandardOffense = (teamPlayers: Player[]) => {
    const getPlayerId = (index: number) => teamPlayers[index]?.number;

    const offense: FormationCell[] = [
      // LoS (Row 1) - The "Wall"
      { playerNumber: getPlayerId(0), row: 1, col: 6 },
      { playerNumber: getPlayerId(1), row: 1, col: 7 },
      { playerNumber: getPlayerId(2), row: 1, col: 8 },
      
      // The "Chain Stack" (Right side)
      { playerNumber: getPlayerId(3), row: 2, col: 8 },
      { playerNumber: getPlayerId(4), row: 3, col: 8 },
      { playerNumber: getPlayerId(5), row: 4, col: 8 },

      // The Scorer (In the pocket)
      { playerNumber: getPlayerId(6), row: 2, col: 7 }, 

      // Wide / Support
      { playerNumber: getPlayerId(7), row: 2, col: 2 }, // Wide Left
      { playerNumber: getPlayerId(8), row: 2, col: 13 }, // Wide Right
      { playerNumber: getPlayerId(9), row: 5, col: 7 }, // Thrower/Safety
      { playerNumber: getPlayerId(10), row: 6, col: 4 },
    ].filter(c => c.playerNumber !== undefined);

    setLayout(offense);
  };

  const handleResetDefense = () => {
    // Standard 3-4-4 Defense Setup
    const defense: FormationCell[] = [
      // LoS (Row -1)
      { playerNumber: 1, row: -1, col: 7 }, // Center
      { playerNumber: 2, row: -1, col: 6 },
      { playerNumber: 3, row: -1, col: 8 },
      
      // Backfield
      { playerNumber: 4, row: -2, col: 4 }, // Wide Left
      { playerNumber: 5, row: -2, col: 11 }, // Wide Right
      { playerNumber: 6, row: -3, col: 7 }, // Safety
    ];
    setOpponentLayout(defense);
  };

  const handleSelectPreset = (key: string) => {
    const team = PRESET_TEAMS[key]();
    setSelectedTeam(team);
    setupStandardOffense(team.players);
    handleResetDefense();
  };

  const handleSelectUserTeam = (id: string) => {
    const team = userTeams.find(t => t.id === id);
    if (team) {
      setSelectedTeam(team);
      setupStandardOffense(team.players);
      handleResetDefense();
    }
  };

  // Helper to get abbreviation for the counter
  const getPlayerLabel = (id: number) => {
    if (!selectedTeam) return String(id);
    const player = selectedTeam.players.find(p => p.number === id);
    if (!player) return String(id);
    
    // Map position to abbreviation
    const position = player.position.toLowerCase();
    if (position.includes('gutter')) return 'GR';
    if (position.includes('blitzer')) return 'BL';
    if (position.includes('thrower')) return 'TH';
    if (position.includes('lineman')) return 'LM';
    if (position.includes('ogre')) return 'OG';
    if (position.includes('rat')) return 'RO';
    if (position.includes('wardancer')) return 'WD';
    if (position.includes('catcher')) return 'CA';
    if (position.includes('treeman')) return 'TM';
    
    // Fallback: First 2 letters of position
    return player.position.substring(0, 2).toUpperCase();
  };

  // NEW HELPER: Handle row math skipping 0
  const addRows = (startRow: number, delta: number) => {
    let result = startRow + delta;
    // If we crossed 0 (e.g. -1 + 1 = 0, or -2 + 3 = 1)
    // Actually, simpler: just step one by one
    if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        result = startRow + 1;
        if (result === 0) result = 1;
        startRow = result;
      }
    } else if (delta < 0) {
      for (let i = 0; i > delta; i--) {
        result = startRow - 1;
        if (result === 0) result = -1;
        startRow = result;
      }
    }
    return startRow;
  };

  // Helper to calculate the 3 valid push squares
  const getValidPushSquares = (r1: number, c1: number, r2: number, c2: number) => {
    // Calculate visual delta (ignoring the 0 skip for direction, but we need it for magnitude)
    // Actually, just determining direction is enough.
    
    // Direction Vector
    let dRow = 0;
    if (r2 > r1) {
        // Moving "Down" (towards positive)
        // Check if we crossed 0? 
        // If r1 is -1 and r2 is 1, that is 1 step.
        if (r1 === -1 && r2 === 1) dRow = 1;
        else dRow = r2 - r1;
    } else if (r2 < r1) {
        // Moving "Up"
        if (r1 === 1 && r2 === -1) dRow = -1;
        else dRow = r2 - r1;
    }
    
    const dCol = c2 - c1;
    
    const valid: { row: number; col: number }[] = [];

    if (dRow !== 0 && dCol === 0) {
      // STRAIGHT PUSH (Vertical)
      // 1. Straight Back
      valid.push({ row: addRows(r2, dRow), col: c2 });
      // 2. Diagonal Left Back
      valid.push({ row: addRows(r2, dRow), col: c2 - 1 });
      // 3. Diagonal Right Back
      valid.push({ row: addRows(r2, dRow), col: c2 + 1 });
    } 
    else if (dRow === 0 && dCol !== 0) {
      // STRAIGHT PUSH (Horizontal)
      // 1. Straight Back
      valid.push({ row: r2, col: c2 + dCol });
      // 2. Diagonal Up Back
      valid.push({ row: addRows(r2, -1), col: c2 + dCol });
      // 3. Diagonal Down Back
      valid.push({ row: addRows(r2, 1), col: c2 + dCol });
    } 
    else {
      // DIAGONAL PUSH
      // 1. Straight Back (Diagonal)
      valid.push({ row: addRows(r2, dRow), col: c2 + dCol });
      // 2. Orthogonal Vertical (Same Col, Next Row)
      valid.push({ row: addRows(r2, dRow), col: c2 });
      // 3. Orthogonal Horizontal (Same Row, Next Col)
      valid.push({ row: r2, col: c2 + dCol });
    }

    // Filter out invalid board coordinates
    return valid.filter(s => Math.abs(s.row) <= 7 && s.col >= 1 && s.col <= 15);
  };

  const handleDrop = (id: number, row: number, col: number, isOpponent?: boolean) => {
    // Check Offense
    const playerHere = layout.find(c => c.row === row && c.col === col);
    
    // Check Defense
    const opponentHere = opponentLayout.find(c => c.row === row && c.col === col);

    // Self-Check Logic
    const isSelfOffense = !isOpponent && playerHere && playerHere.playerNumber === id;
    const isSelfDefense = isOpponent && opponentHere && opponentHere.playerNumber === id;

    const isOccupied = (!!playerHere && !isSelfOffense) || (!!opponentHere && !isSelfDefense);

    if (isPlayMode) {
      if (isOccupied) {
        // 1. Find Attacker
        const attacker = layout.find(c => c.playerNumber === id) || opponentLayout.find(c => c.playerNumber === id);
        
        if (!attacker) return;

        // 2. Calculate Valid Push Squares
        const validSquares = getValidPushSquares(attacker.row, attacker.col, row, col);

        // 3. Enter Push Selection Mode
        setPushSelection({
          attackerId: id,
          defenderId: playerHere ? playerHere.playerNumber : opponentHere!.playerNumber,
          defenderIsOpponent: !!opponentHere,
          fromRow: attacker.row,
          fromCol: attacker.col,
          targetRow: row,
          targetCol: col,
          validSquares
        });
        
        return; 
      }
    } else {
      // SETUP MODE CONSTRAINTS
      
      // 1. Prevent Stacking
      if (isOccupied) return;

      // 2. Enforce Half Restrictions
      if (isOpponent) {
        if (row > 0) return;
      } else {
        if (row < 0) return;
      }
    }

    // Normal Move Logic (if not occupied)
    if (isOpponent) {
      setOpponentLayout(prev => {
        const filtered = prev.filter(c => c.playerNumber !== id);
        return [...filtered, { playerNumber: id, row, col }];
      });
    } else {
      setLayout(prev => {
        const filtered = prev.filter(c => c.playerNumber !== id);
        return [...filtered, { playerNumber: id, row, col }];
      });
      setSelectedId(id);
    }
  };

  const handleSquareClick = (row: number, col: number) => {
    if (pushSelection) {
      // Check if clicked square is a valid push destination
      const isValid = pushSelection.validSquares.some(s => s.row === row && s.col === col);
      
      if (isValid) {
        // EXECUTE THE PUSH
        
        // 1. Move Defender to Clicked Square
        if (pushSelection.defenderIsOpponent) {
          setOpponentLayout(prev => {
            const filtered = prev.filter(c => c.playerNumber !== pushSelection.defenderId);
            return [...filtered, { playerNumber: pushSelection.defenderId, row, col }];
          });
        } else {
          setLayout(prev => {
            const filtered = prev.filter(c => c.playerNumber !== pushSelection.defenderId);
            return [...filtered, { playerNumber: pushSelection.defenderId, row, col }];
          });
        }

        // 2. Move Attacker to Defender's Old Spot (Follow Up)
        const isAttackerOpponent = opponentLayout.some(c => c.playerNumber === pushSelection.attackerId);
        
        if (isAttackerOpponent) {
           setOpponentLayout(prev => {
             const filtered = prev.filter(c => c.playerNumber !== pushSelection.attackerId);
             return [...filtered, { playerNumber: pushSelection.attackerId, row: pushSelection.targetRow, col: pushSelection.targetCol }];
           });
        } else {
           setLayout(prev => {
             const filtered = prev.filter(c => c.playerNumber !== pushSelection.attackerId);
             return [...filtered, { playerNumber: pushSelection.attackerId, row: pushSelection.targetRow, col: pushSelection.targetCol }];
           });
        }

        // Reset Push Selection
        setPushSelection(null);
      }
    }
  };

  const handleReturn = (id: number) => {
    setLayout(prev => prev.filter(c => c.playerNumber !== id));
    setSelectedId(id);
  };

  const handleCounterClick = (id: number) => {
    setSelectedId(id);
  };

  if (selectedTeam) {
    return (
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <Header status={`OTT Simulator: ${selectedTeam.teamName} ${isPlayMode ? '(PLAY MODE)' : '(SETUP)'}`} />
        <div className="page-content">
          
          {/* Left Panel: Roster Bench */}
          <div className="roster-bench">
            <RosterBench
              players={selectedTeam.players}
              layout={layout}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onReturn={handleReturn}
            />
          </div>

          {/* Center Panel: The Grid */}
          <div className="board-wrapper" style={{ marginTop: '20px' }}>
            <OTTGrid
              layout={layout}
              opponentLayout={opponentLayout}
              onDrop={handleDrop}
              getPlayerLabel={getPlayerLabel}
              onCounterClick={handleCounterClick}
              highlights={pushSelection ? pushSelection.validSquares : []}
              onSquareClick={handleSquareClick}
            />
          </div>

          {/* Right Panel: Controls */}
          <div className="formations-panel">
            <h3>Controls</h3>
            
            {/* The Big Start Button */}
            <button 
              className="home-btn" 
              style={{ 
                backgroundColor: isPlayMode ? '#d32f2f' : '#2e7d32', 
                marginBottom: '20px',
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }} 
              onClick={() => setIsPlayMode(!isPlayMode)}
            >
              {isPlayMode ? 'STOP / RESET' : 'START PLAY'}
            </button>

            {!isPlayMode && (
              <>
                <button className="home-btn" onClick={handleResetDefense}>
                  Reset Defense
                </button>
                <button className="home-btn" style={{ marginTop: '10px' }} onClick={() => setupStandardOffense(selectedTeam.players)}>
                  Reset Offense
                </button>
                <button className="home-btn" style={{ marginTop: '10px', backgroundColor: '#666' }} onClick={() => setSelectedTeam(null)}>
                  Change Team
                </button>
              </>
            )}
            
            {isPlayMode && (
               <div style={{ padding: '10px', backgroundColor: '#eee', borderRadius: '4px', marginTop: '10px' }}>
                 <p style={{ margin: 0, fontSize: '0.9rem' }}>Drag a player onto an opponent to start a Chain Push.</p>
                 {pushSelection && <p style={{ color: 'red', fontWeight: 'bold' }}>Select a highlighted square to push!</p>}
               </div>
            )}
          </div>

        </div>
      </DndProvider>
    );
  }

  return (
    <>
      <Header status="OTT Simulator - Select Team" />
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem', marginTop: '2rem' }}>
        
        <div className="selection-card" style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '8px', width: '300px' }}>
          <h3>Quick Play (Presets)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.keys(PRESET_TEAMS).map(key => (
              <button key={key} className="home-btn" onClick={() => handleSelectPreset(key)}>
                Play as {key}
              </button>
            ))}
          </div>
        </div>

        <div className="selection-card" style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '8px', width: '300px' }}>
          <h3>Load My Team</h3>
          {userTeams.length === 0 ? (
            <p>No teams found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {userTeams.map(team => (
                <button key={team.id} className="home-btn" onClick={() => handleSelectUserTeam(team.id)}>
                  {team.teamName}
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="home-btn" style={{ backgroundColor: '#666' }} onClick={() => navigate('/')}>
          Back to Home
        </button>
      </div>
    </>
  );
}